import type { FeeFacts, SiteConfig } from "../api/types";
import { AT_LAUNCH } from "./facts";

/**
 * Every sentence the site writes about fees, derived from GET /api/config so no
 * page can drift from another and nothing is hardcoded.
 *
 * The structure: a trade pays the launchpad's platform fee plus a creator tax
 * the creator set once on the Pons v2 create form. The creator tax stacks on
 * top of the platform fee and goes to the creator wallet. Half of it is the
 * pledge. The creator tax is immutable after launch, so it is read from the
 * server and never written into the code.
 *
 * config.fees is null until the owner sets the creator tax, and can be missing
 * on a worker that predates the field, so each helper falls back to a sentence
 * that promises nothing.
 */

/** The Pons v2 platform fee, used only until GET /api/config resolves. */
const PLATFORM_PCT_FALLBACK = 1;

/** Shown for the money figure while the creator tax is unpublished. */
const EXAMPLE_AT_LAUNCH = "the exact figure is published at launch";

/** The trading volume the worked example is quoted on. */
const EXAMPLE_VOLUME = "US$1,000,000";

/** Up to two decimals, trailing zeros trimmed: 2 to "2%", 0.5 to "0.5%". */
function formatPct(value: number): string {
  return `${value.toFixed(2).replace(/\.?0+$/, "")}%`;
}

/** Whole US dollars with thousands separators: 10000 to "US$10,000". */
function formatUsd(value: number): string {
  return `US$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value)}`;
}

/** A number the server actually sent, or null so the caller can say nothing. */
function pct(facts: FeeFacts | null, key: keyof FeeFacts): number | null {
  const value = facts?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function feeFacts(config: SiteConfig | null): FeeFacts | null {
  return config?.fees ?? null;
}

/** The launchpad's own fee. Known before launch, so it always renders. */
export function platformFee(config: SiteConfig | null): string {
  return formatPct(pct(feeFacts(config), "platformPct") ?? PLATFORM_PCT_FALLBACK);
}

/** What a trader pays in total, platform fee plus creator tax. */
export function tradersPay(config: SiteConfig | null): string {
  const value = pct(feeFacts(config), "tradersPayPct");
  return value === null ? AT_LAUNCH : `${formatPct(value)} per trade`;
}

/** The creator tax, which is the creator's share and the base of the pledge. */
export function creatorShare(config: SiteConfig | null): string {
  const value = pct(feeFacts(config), "creatorTaxPct");
  return value === null ? AT_LAUNCH : `${formatPct(value)} of every trade`;
}

/** Half of the creator's share, expressed against trading volume. */
export function pledgeCeiling(config: SiteConfig | null): string {
  const value = pct(feeFacts(config), "pledgePctOfVolume");
  return value === null ? AT_LAUNCH : `${formatPct(value)} of trading volume`;
}

/** The same pledge in money people can picture. */
export function pledgeExample(config: SiteConfig | null): string {
  const value = pct(feeFacts(config), "examplePerMillionUsd");
  if (value === null) return EXAMPLE_AT_LAUNCH;
  return `${EXAMPLE_VOLUME} of trading donates about ${formatUsd(value)}, paid in Canadian dollars at the rate on the day`;
}
